import { Component } from "react";
import * as React from "react";
import { DatePicker, Dropdown, IDropdownOption } from "office-ui-fabric-react";
import ApiService from "./ApiService";
import H from "history";
import moment from 'moment';
import { parseQueryString } from "../utils/Utils";
import AcademicYearPicker from "./AcademicYearPicker";

const DATE_PARAM_FORMAT = "YYYY-MM-DDTHH:mm:ss";

interface AnalyticsDateControlsProps {
    history: H.History;
    onSelectedKeyChange?: (startDate: string, endDate: string) => void;
    classNameOverride?: string;
}

interface AnalyticsDateControlsState {
    selectedKey: string;
    startDate: Date;
    endDate: Date;
}

export default class AnalyticsDateControls extends Component<AnalyticsDateControlsProps, AnalyticsDateControlsState>
{
    public static readonly ASSIGNMENT_ANALYTICS_MESSAGE = (<span className={"text-tiny p-top-05rem"}>Data updated weekly from your school.<br />'Active' assignments were still outstandng when this data was updated.</span>);
    private static defaultOptionKey: string = "last90Days";

    private static options: IDropdownOption[] = [
        {
            text: "This Month",
            key: "thisMonth",
            data: {
                startDate: AnalyticsDateControls.SetStartTime(moment().startOf('month')),
                endDate: AnalyticsDateControls.SetEndTime(moment().endOf('month'))
            }
        },
        {
            text: "Last Month",
            key: "lastMonth",
            data: {
                startDate: AnalyticsDateControls.SetStartTime(moment().subtract(1, 'month').startOf('month')),
                endDate: AnalyticsDateControls.SetEndTime(moment().subtract(1, 'month').endOf('month'))
            }
        },
        {
            text: "Last 90 days",
            key: "last90Days",
            data: {
                startDate: AnalyticsDateControls.SetStartTime(moment().subtract(90, 'days')),
                endDate: AnalyticsDateControls.SetEndTime(moment())
            }
        },
        {
            text: "Last 6 Months",
            key: "last6Months",
            data: {
                startDate: AnalyticsDateControls.SetStartTime(moment().subtract(6, 'month')),
                endDate: AnalyticsDateControls.SetEndTime(moment())
            }
        },
        {
            text: "Custom",
            key: "custom",
            data: {
            }
        }
    ];

    private static SetStartTime(momentDate: any): string {
        return momentDate
            .startOf('day')
            .format(DATE_PARAM_FORMAT);
    }

    private static SetEndTime(momentDate: any): string {
        return momentDate
            .endOf('day')
            .format(DATE_PARAM_FORMAT);
    }

    private static findOptionKeyForDates(startDate: string, endDate: string): string {
        const matchingOption = AnalyticsDateControls.options.filter((option: IDropdownOption) => {
            return option.data.startDate === startDate && option.data.endDate === endDate;
        })[0];

        if (matchingOption) {
            return matchingOption.key as string;
        }
        else {
            return "custom";
        }
    }

    public static fromQueryStringOrDefault(queryString: string): { startDate: string, endDate: string } {
        const queryObj = parseQueryString(queryString);

        const startDate = queryObj["startDate"];
        const endDate = queryObj["endDate"];

        if (startDate && endDate) {
            return {
                startDate,
                endDate
            };
        }
        else {
            return AnalyticsDateControls.options.filter(o => o.key === this.defaultOptionKey)[0].data;
        }
    }

    public static SetDateRangeToAcademic(history: H.History, academicYear: number) {
        var startDate = AcademicYearPicker.startDateForAcademicYear(academicYear).format(DATE_PARAM_FORMAT);
        var endDate = AcademicYearPicker.endDateForAcademicYear(academicYear).format(DATE_PARAM_FORMAT);

        AnalyticsDateControls.updateDatesInQueryString(history, startDate, endDate);
    }

    private static updateDatesInQueryString(history: H.History, startDate: string, endDate: string) {
        var queryObject = parseQueryString(history.location.search);

        if (queryObject["startDate"] !== startDate || queryObject["endDate"] !== endDate) {

            queryObject["startDate"] = startDate;
            queryObject["endDate"] = endDate;

            history.replace({ search: ApiService.buildQueryString(queryObject) });
        }
    }

    constructor(props: Readonly<AnalyticsDateControlsProps>) {
        super(props);

        const { startDate, endDate } = AnalyticsDateControls.fromQueryStringOrDefault(props.history.location.search);
        const selectedKey = AnalyticsDateControls.findOptionKeyForDates(startDate, endDate);

        this.state = {
            startDate: moment(startDate).toDate(),
            endDate: moment(endDate).toDate(),
            selectedKey
        };

        this.onDatesUpdated();
    }

    componentDidUpdate(prevProps: Readonly<AnalyticsDateControlsProps>, prevState: Readonly<AnalyticsDateControlsState>, snapshot?: any): void {
        if (this.state.startDate !== prevState.startDate || this.state.endDate !== prevState.endDate) {

            var startDate = moment(this.state.startDate).format(DATE_PARAM_FORMAT);
            var endDate = moment(this.state.endDate).format(DATE_PARAM_FORMAT)

            AnalyticsDateControls.updateDatesInQueryString(this.props.history, startDate, endDate);

            this.onDatesUpdated();
        }
    }

    private onDatesUpdated() {
        if (this.props.onSelectedKeyChange) {
            this.props.onSelectedKeyChange(
                moment(this.state.startDate).format(DATE_PARAM_FORMAT),
                moment(this.state.endDate).format(DATE_PARAM_FORMAT));
        }
    }

    public render() {

        
        var className = this.props.classNameOverride ?? "analytics-date-controls"
        
        return (
            <div className={className}>

                <Dropdown
                    selectedKey={this.state.selectedKey}
                    label={"Date range"}
                    options={AnalyticsDateControls.options}
                    onChange={(event, option: any) => {
                        if (option.key === "custom") {
                            this.setState({
                                selectedKey: option.key,
                            });
                        }
                        else {
                            this.setState({
                                selectedKey: option.key,
                                startDate: moment(option.data.startDate).toDate(),
                                endDate: moment(option.data.endDate).toDate()
                            });
                        }
                    }}
                />

                <div className={`${className}__custom`} style={{ display: this.state.selectedKey === "custom" ? "flex" : "none" }}>
                    <DatePicker
                        label={"From"}
                        value={this.state.startDate}
                        onSelectDate={(v) => {
                            this.setState({
                                startDate: moment(v!).startOf('day').toDate()
                            });
                        }}
                    />

                    <DatePicker
                        label={"To"}
                        value={this.state.endDate}
                        onSelectDate={(v) => {
                            this.setState({
                                endDate: moment(v!).endOf('day').toDate()
                            });
                        }}
                    />
                </div>

                {this.props.children}
            </div>
        );
    }
}